import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import SendBird from "sendbird";
import {
  sbLeaveGroupChannel,
  sbDeleteGroupChannel,
  sbSetGroupChannelPushTriggerOption,
  sbRegisterGroupChannelOperator,
  sbRemoveGroupChannelOperator,
  sbBanGroupChannelMember,
  sbCreateGroupChannelMembersListQuery,
  sbGetGroupChannelDetail,
} from "../utils/sb";
import { fetchChannels, setNotificationTriggerOption } from "./clubhouseSlice";

type ClubhouseDetailState = {
  memberQuery: SendBird.GroupChannelMemberListQuery | null;
  members: SendBird.User[];
  isLoading: boolean;
  isRefreshing: boolean;
  isFetching: boolean;
  isSearching: boolean;
  error: string | null;
  channel: SendBird.GroupChannel;
  currentPushTriggerOption: "default" | "off";
};

const clubhouseDetailSlice = createSlice({
  name: "clubhouseDetail",
  initialState: {
    memberQuery: null,
    isLoading: false,
    isRefreshing: false,
    isFetching: false,
    isSearching: false,
    error: null,
    members: [],
    channel: {} as SendBird.GroupChannel,
    currentPushTriggerOption: "default",
  } as ClubhouseDetailState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload;
    },
    setFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setQuery: (state, action) => {
      state.memberQuery = action.payload;
    },
    setChannel: (state, action) => {
      state.channel = action.payload;
    },
    setCurrentPushTriggerOption: (state, action) => {
      state.currentPushTriggerOption = action.payload;
    },
    setMembers: (
      state,
      {
        payload: { members, reset = false },
      }: PayloadAction<{
        members: SendBird.User[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.members = members;
      } else {
        const mergedMembers = [...state.members, ...members];
        state.members = mergedMembers;
      }
    },
    refresh: (state) => {
      state.isRefreshing = true;
      state.error = null;
    },
    clear: (state) => {
      state.memberQuery = null;
      state.isLoading = false;
      state.isRefreshing = false;
      state.isFetching = false;
      state.isSearching = false;
      state.error = null;
      state.members = [];
      state.channel = {};
      state.currentPushTriggerOption = "default";
    },
  },
});

export const {
  setLoading,
  setRefreshing,
  setFetching,
  setSearching,
  setError,
  setMembers,
  setQuery,
  refresh,
  setCurrentPushTriggerOption,
  setChannel,
  clear,
} = clubhouseDetailSlice.actions;

export const leaveChannel = (channelUrl) => async (dispatch) => {
  try {
    await sbLeaveGroupChannel(channelUrl);
    dispatch(fetchChannels());
    // showToast({
    //   type: 'success',
    //   title: 'success',
    //   subTitle: 'clubouse exited successfully',
    // });
  } catch (error) {
    console.log(error);
    // showToast({
    //   type: 'error',
    //   title: 'error',
    //   subTitle: 'an error occurred while exiting clubhouse',
    // });
  }
};

export const deleteChannel = (channelUrl) => async (dispatch) => {
  try {
    await sbDeleteGroupChannel(channelUrl);
    dispatch(fetchChannels());
    // showToast({
    //   type: 'success',
    //   title: 'success',
    //   subTitle: 'clubouse deleted successfully',
    // });
  } catch (error) {
    console.log(error);
    // showToast({
    //   type: 'error',
    //   title: 'error',
    //   subTitle: 'an error occurred while deleting clubhouse',
    // });
  }
};

export const setChannelNotificationPref =
  (channelUrl = "", currentPushTriggerOption = "") =>
  async (dispatch) => {
    const isChannelMuted = currentPushTriggerOption === "off";
    const newTriggerOption = isChannelMuted ? "default" : "off";
    try {
      await sbSetGroupChannelPushTriggerOption(channelUrl, newTriggerOption);
      // showToast({
      //   type: 'success',
      //   title: 'success',
      //   subTitle: 'notification preferences updated successfully',
      // });
      dispatch(setCurrentPushTriggerOption(newTriggerOption));
      dispatch(
        setNotificationTriggerOption({
          url: channelUrl,
          triggerOption: newTriggerOption,
        })
      );
    } catch (error) {
      console.log(error);
      // showToast({
      //   type: 'error',
      //   title: 'error',
      //   subTitle: 'an error occurred while updating notification preferences',
      // });
    }
  };

export const registerAsOperator =
  (channelUrl, userId) => async (dispatch, getState) => {
    try {
      const {
        clubhouseDetail: { members },
      } = getState();
      await sbRegisterGroupChannelOperator(channelUrl, [userId]);
      const updatedMembers = members.map((member) => {
        if (member.userId === userId) {
          member.role = "operator";
        }
        return member;
      });
      dispatch(setMembers({ members: updatedMembers, reset: true }));
      // showToast({
      //   type: 'success',
      //   title: 'success',
      //   subTitle: 'member promoted to co-founder',
      // });
    } catch (error) {
      console.log(error);
      // showToast({
      //   type: 'error',
      //   title: 'error',
      //   subTitle:
      //     'an error occurred while promoting member to co-founder. please try again later',
      // });
    }
  };

export const removeAsOperator =
  (channelUrl, userId) => async (dispatch, getState) => {
    try {
      const {
        clubhouseDetail: { members },
      } = getState();
      await sbRemoveGroupChannelOperator(channelUrl, [userId]);
      const updatedMembers = members.map((member) => {
        if (member.userId === userId) {
          member.role = "none";
        }
        return member;
      });
      dispatch(setMembers({ members: updatedMembers, reset: true }));
      // showToast({
      //   type: 'success',
      //   title: 'success',
      //   subTitle: 'member removed as founder',
      // });
    } catch (error) {
      console.log(error);
      // showToast({
      //   type: 'error',
      //   title: 'error',
      //   subTitle:
      //     'an error occured while removing member as a founder. please try again later',
      // });
    }
  };

export const banMember = (channelUrl, userId) => async (dispatch, getState) => {
  try {
    const {
      clubhouseDetail: { members },
    } = getState();
    await sbBanGroupChannelMember(channelUrl, userId);
    const updatedMembers = members.filter((member) => member.userId !== userId);
    dispatch(setMembers({ members: updatedMembers, reset: true }));
    // showToast({
    //   type: 'success',
    //   title: 'success',
    //   subTitle: 'member banned from this clubhouse',
    // });
  } catch (error) {
    console.log(error);
    // showToast({
    //   type: 'error',
    //   title: 'error',
    //   subTitle:
    //     'an error occurred while banning member. please try again later',
    // });
  }
};

export const fetchNextMembers = () => async (dispatch, getState) => {
  try {
    const {
      clubhouseDetail: { memberQuery },
    } = getState();
    dispatch(setFetching(true));
    const fetchedMembers = await memberQuery?.next();
    dispatch(setMembers({ members: fetchedMembers }));
  } catch (error) {
    console.log("error", error);
  } finally {
    dispatch(setFetching(false));
  }
};

export const fetchMembers = (channelUrl) => async (dispatch) => {
  dispatch(setError(null));
  try {
    dispatch(setMembers({ members: [], reset: true }));
    const query = await sbCreateGroupChannelMembersListQuery(
      channelUrl,
      "operator"
    );
    dispatch(setQuery(query));
  } catch (error) {
    dispatch(setError(error?.message));
  }
};

export const fetchGroupChannelDetails = (channelUrl) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const groupChannel = await sbGetGroupChannelDetail(channelUrl);
    dispatch(fetchMembers(channelUrl));
    dispatch(setChannel(groupChannel));
    dispatch(setCurrentPushTriggerOption(groupChannel?.myPushTriggerOption));
  } catch (error) {
    dispatch(setError(error?.message));
  } finally {
    dispatch(setLoading(false));
  }
};

export default clubhouseDetailSlice.reducer;
